import {testIdProps} from '@github-ui/test-id-props'
import {ArchiveIcon, KebabHorizontalIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useRef, useState} from 'react'

import type {Recap} from '../../api/scribe/types'
import styles from './recap-archive.module.css'
import {formatRecapDateRange} from './recap-summary'

interface ArchiveItemProps {
  recap: Recap
  onView: (recapId: number) => void
  onDelete: (recapId: number) => void
}

function ArchiveItem({recap, onView, onDelete}: ArchiveItemProps) {
  const actionButtonRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <ActionList.Item onSelect={() => onView(recap.id)} {...testIdProps('archive-item')} className={styles.archiveItem}>
      <div>
        <h3 className={styles.archiveTitle}>
          {(() => {
            const {dateString, showWeekOf} = formatRecapDateRange(recap)
            return showWeekOf ? `Week of ${dateString}` : `${dateString}`
          })()}
        </h3>
        <div className={styles.archiveDetails}>
          {recap.description ? recap.description : 'No description available'}
        </div>
      </div>
      <ActionList.TrailingAction
        onClick={() => setMenuOpen(true)}
        icon={KebabHorizontalIcon}
        label="Recap options"
        ref={actionButtonRef}
      />
      <ActionMenu anchorRef={actionButtonRef} open={menuOpen} onOpenChange={setMenuOpen}>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.Item
              variant="danger"
              onSelect={e => {
                e.stopPropagation()
                onDelete(recap.id)
              }}
            >
              <ActionList.LeadingVisual>
                <TrashIcon />
              </ActionList.LeadingVisual>
              Delete
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </ActionList.Item>
  )
}

interface RecapArchiveProps {
  recaps: Array<Recap> | undefined
  isLoading: boolean
  error: Error | null
  onView: (recapId: number) => void
  onDelete: (recapId: number) => void
}

export function RecapArchive({recaps, isLoading, error, onView, onDelete}: RecapArchiveProps) {
  return (
    <div role="tabpanel" id="archive-panel" aria-labelledby="archive-tab">
      {!isLoading && !error && recaps?.length ? (
        <div className={styles.archiveContent}>
          <ActionList className={styles.archiveList}>
            {recaps.map(recap => (
              <ArchiveItem key={recap.id} recap={recap} onView={onView} onDelete={onDelete} />
            ))}
          </ActionList>
        </div>
      ) : (
        !isLoading &&
        !error && (
          <div className={styles.emptyState}>
            <Blankslate>
              <Blankslate.Visual>
                <ArchiveIcon size="medium" />
              </Blankslate.Visual>
              <Blankslate.Heading>No archived recaps</Blankslate.Heading>
              <Blankslate.Description>Past recaps will appear here when available.</Blankslate.Description>
            </Blankslate>
          </div>
        )
      )}
    </div>
  )
}
